/**
 * The signed-in user, as the backend describes them.
 *
 * `role` is the ONLY thing the UI may branch on. The API token carries no role —
 * its claims are the username, an issued-at and an expiry, nothing else — and the
 * server re-reads the role from the database on every request. So decoding the
 * JWT to decide what to show would be reading a claim that does not exist.
 */

export const USER_ROLES = ['ADMIN', 'AAK', 'USER', 'KGA_USER', 'INTEGRATOR', 'OPERATOR'] as const;

export type UserRole = (typeof USER_ROLES)[number];

/** The company a user belongs to. Set at creation and not editable afterwards. */
export interface CompanyInfo {
  id: number;
  companyName: string | null;
  companyType: string | null;
}

/**
 * Contact details, returned beside the user.
 *
 * Note the name of the last field: it comes back as `iin` and is WRITTEN as `tin`.
 * The two are the same number — see `UserRegisterCard`.
 */
export interface UserCard {
  phoneNumber: string | null;
  email: string | null;
  addressLine: string | null;
  iin: string | null;
}

export interface UserInfo {
  id: number;
  /**
   * The login, and the identity the token is issued against. Set at creation and
   * never editable — no edit endpoint has a field for it.
   */
  username: string;
  firstName: string | null;
  middleName: string | null;
  lastName: string | null;
  /** A user has exactly one role, or none at all — in which case everything 403s. */
  role: UserRole | null;
  /**
   * Whether the account may sign in. Read it through `isUserActive`, never
   * directly: both spellings are declared and only one of them arrives.
   */
  isActive?: boolean;
  active?: boolean;
  isCompanyHead?: boolean;
  companyHead?: boolean;
  created: string | null;
  updated: string | null;
  companyInfo: CompanyInfo | null;
  userCard: UserCard | null;
}

/**
 * Whether this account may sign in — `false` is what "deleted" means here.
 *
 * Both names are read because the two descriptions of this response disagree: the
 * API documentation shows `isActive`, and the type this app was originally built
 * from the login response carries `active`. They are the same Jackson-serialized
 * field and which name arrives depends on the getter that produced it.
 *
 * Picking one and being wrong would invert the state on every row of the one screen
 * whose job is to change it — every working account shown as blocked, and the
 * button beside it offering to do the opposite of what it says. Reading whichever
 * arrived cannot be wrong either way.
 *
 * Neither present means the response is not what either description says, and
 * active is the normal case: showing a working account as blocked would send an
 * administrator to re-enable something that was never off.
 */
export const isUserActive = (user: UserInfo): boolean => user.isActive ?? user.active ?? true;

/** Whether the user heads their company. Same two-name problem as above. */
export const isCompanyHead = (user: UserInfo): boolean =>
  user.isCompanyHead ?? user.companyHead ?? false;

/**
 * The body of `POST /user/add?companyId={id}`.
 *
 * `username` and `password` are the only required fields; the password has a
 * six-character minimum the server enforces and rejects with a `DataError`.
 *
 * **`tin` is the field the response calls `iin`.** The request and the response
 * genuinely use different names for the same number, so a form that reads
 * `userCard.iin` and writes `tin` is correct rather than a typo waiting to be
 * "fixed".
 */
export interface UserRegisterCard {
  username: string;
  password: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  role?: UserRole;
  phoneNumber?: string;
  email?: string;
  addressLine?: string;
  tin?: string;
}

/**
 * The body of `POST /user/edit/id/{userId}` — an administrator editing anyone.
 *
 * **Only the fields present are applied**, so `{ middleName: 'gg' }` is a complete
 * request and everything else is left alone. There is no `username` here: it is the
 * identity the token is issued against and cannot be changed.
 *
 * `role` is optional and takes effect. It used to be required AND ignored — a
 * client that sends it on every edit is describing the old behaviour, so send it
 * only when the role is actually being changed.
 */
export interface UserEditCard {
  firstName?: string;
  middleName?: string;
  lastName?: string;
  role?: UserRole;
  /** Set directly, with no current password — an administrator resetting one. */
  password?: string;
  phoneNumber?: string;
  email?: string;
  addressLine?: string;
  tin?: string;
}

/**
 * The body of `POST /user/me` — a user editing their own account.
 *
 * A DIFFERENT shape from `UserEditCard`, and the difference is the point: there is
 * no `role`, no `isActive`, no `username`. Raising your own privileges through this
 * endpoint is not refused, it is unrepresentable.
 *
 * `newPassword` requires `currentPassword`; a wrong or missing one is refused with a
 * `DataError` on `currentPassword` and nothing is changed, so a session someone else
 * has taken over cannot be used to lock the owner out.
 */
export interface UserProfileCard {
  firstName?: string;
  middleName?: string;
  lastName?: string;
  phoneNumber?: string;
  email?: string;
  addressLine?: string;
  tin?: string;
  currentPassword?: string;
  newPassword?: string;
}

/** A successful login. */
export interface LoginSuccess {
  token: string;
  /**
   * A second, unrelated token for Kibana. NEVER send this to this API: it is
   * signed with the same key, so if `elk.login` happens to exist as an active
   * user, the caller would be authenticated as THAT account with its role.
   */
  elkToken: string | null;
  userInfo: UserInfo;
}

/**
 * A failed login — which also arrives as HTTP 200.
 *
 * `LoginController` builds it with `ResponseEntity.of(Optional.of(...))`, and that
 * is 200 whenever a body is present. Every reason collapses into one shape: wrong
 * password, unknown username, blocked account and server error are
 * indistinguishable. Deliberate for the first three; check the server logs for the
 * fourth.
 */
export interface LoginFailure {
  path: string | null;
  errorCode: string;
  message: string;
}

/**
 * Which roles may reach a screen, taken from SecurityConfig rather than guessed.
 *
 * OPERATOR is absent from every list on purpose: the enum describes it as the role
 * that creates and edits schedules, but SecurityConfig never names it, so it 403s
 * on the very screens it is named for. Listing it here would promise access the
 * server refuses.
 */
/**
 * The board — the landing screen, and the only one a non-administrator has.
 *
 * INTEGRATOR and OPERATOR are absent, so `/schedule` is routed on this rather than
 * on "any signed-in role": without the guard those two would land on a board whose
 * every request answers 403.
 */
export const ROLES_ONLINE_TABLE: readonly UserRole[] = ['ADMIN', 'AAK', 'USER', 'KGA_USER'];

/**
 * What the SERVER allows on `/airport/**` and `/airline/**`.
 *
 * **The app is deliberately stricter**: both dictionary screens are routed
 * ADMIN-only, because every other role works the board and nothing else, and a
 * dictionary they cannot change is a screen with no use for them. This list is kept
 * because it records the server's actual rule — the thing that would have to be
 * re-derived if that product decision is revisited — not because anything routes on
 * it today.
 *
 * Narrowing the UI below what the server permits is always safe. The reverse never
 * is, which is why these lists are copied from SecurityConfig rather than guessed.
 */
export const ROLES_DICTIONARY_READ: readonly UserRole[] = ['ADMIN', 'AAK', 'USER', 'KGA_USER'];
export const ROLES_DICTIONARY_WRITE: readonly UserRole[] = ['ADMIN'];
export const ROLES_ADMIN_ONLY: readonly UserRole[] = ['ADMIN'];

/**
 * A record, not a guard: there is no international-arrivals screen yet.
 *
 * Kept for the same reason as `ROLES_DICTIONARY_READ` — it is what `SecurityConfig`
 * says, and re-deriving it later from the server is the part that goes wrong.
 */
export const ROLES_INTERNATIONAL_ARRIVALS: readonly UserRole[] = ['ADMIN', 'AAK'];

export const hasRole = (user: UserInfo | null, allowed: readonly UserRole[]): boolean =>
  user?.role != null && allowed.includes(user.role);

/** `А. Бисембин`, falling back to the username when a name is not filled in. */
export const displayName = (user: UserInfo | null): string => {
  if (!user) return '';

  const initial = user.firstName ? `${user.firstName[0]}. ` : '';
  return user.lastName ? `${initial}${user.lastName}` : user.username;
};

/**
 * `Бисембин Алдияр К.` — surname first, as a list of people is read here.
 *
 * Empty rather than the username when no part of the name is filled in: this sits
 * beside the username in a table, and repeating it there would read as a name.
 */
export const fullName = (user: UserInfo): string =>
  [user.lastName, user.firstName, user.middleName]
    .filter((part): part is string => typeof part === 'string' && part.trim() !== '')
    .join(' ');
